import { Keyboard } from "@capacitor/keyboard";
import { Capacitor } from "@capacitor/core";

type KeyboardEventCallback = (isOpen: boolean, keyboardHeight?: number) => void;

export class KeyboardHandler {
  private static instance: KeyboardHandler;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private callbacks: KeyboardEventCallback[] = [];
  private listenerHandles: any[] = [];

  private constructor() {}

  public static getInstance(): KeyboardHandler {
    if (!KeyboardHandler.instance) {
      KeyboardHandler.instance = new KeyboardHandler();
    }
    return KeyboardHandler.instance;
  }

  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    this.initializationPromise = (async () => {
      if (Capacitor.isNativePlatform()) {
        const willShowHandle = await Keyboard.addListener(
          "keyboardWillShow",
          (info: { keyboardHeight: number }) => {
            this.callbacks.forEach((cb) => cb(true, info.keyboardHeight));
          },
        );
        const didShowHandle = await Keyboard.addListener(
          "keyboardDidShow",
          (info: { keyboardHeight: number }) => {
            this.callbacks.forEach((cb) => cb(true, info.keyboardHeight));
          },
        );
        const willHideHandle = await Keyboard.addListener(
          "keyboardWillHide",
          () => {
            this.callbacks.forEach((cb) => cb(false, 0));
          },
        );
        const didHideHandle = await Keyboard.addListener(
          "keyboardDidHide",
          () => {
            this.callbacks.forEach((cb) => cb(false, 0));
          },
        );
        this.listenerHandles.push(
          willShowHandle,
          willHideHandle,
          didHideHandle,
          didShowHandle,
        );
      }
      this.initialized = true;
    })();

    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  public async hideKeyboard(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Keyboard.hide();
    }
  }

  public async showKeyboard(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Keyboard.show();
    }
  }

  public onKeyboardChange(cb: KeyboardEventCallback) {
    this.callbacks.push(cb);
  }

  public offKeyboardChange(cb: KeyboardEventCallback) {
    this.callbacks = this.callbacks.filter((fn) => fn !== cb);
  }

  public async destroy(): Promise<void> {
    if (!this.initialized) {
      return;
    }

    for (const handle of this.listenerHandles) {
      await handle.remove();
    }
    this.listenerHandles = [];
    this.callbacks = [];
    this.initialized = false;
  }
}
